<template>
  <div class="admin-dom-wrapper">
    <admin-nav active="dossiers" />

    <b-container fluid>
      <b-card>
        <h4 class="mb-2">
          Dossiers de domiciliation
        </h4>

        <b-row class="mb-2">
          <b-col md="2">
            <label>État</label>
            <b-form-select
              v-model="filters.etat"
              :options="optionsEtat"
              @change="loadList"
            />
          </b-col>
          <b-col md="2">
            <label>Mandat</label>
            <b-form-select
              v-model="filters.mandat"
              :options="optionsMandat"
              @change="loadList"
            />
          </b-col>
          <b-col md="2">
            <label>Traité</label>
            <b-form-select
              v-model="filters.traite"
              :options="optionsTraite"
              @change="loadList"
            />
          </b-col>
          <b-col md="2">
            <label>Archivé</label>
            <b-form-select
              v-model="filters.archive"
              :options="optionsArchive"
              @change="loadList"
            />
          </b-col>
          <b-col md="4">
            <label>Recherche</label>
            <b-form-input
              v-model="filters.q"
              placeholder="Nom, email, raison sociale"
              @input="onSearch"
            />
          </b-col>
        </b-row>

        <!-- Barre d'actions en masse : n'apparaît qu'une fois une sélection
             faite, pour ne pas encombrer l'écran le reste du temps. -->
        <b-alert
          :show="selection.length > 0"
          variant="primary"
          class="d-flex flex-wrap align-items-center justify-content-between mb-1 px-2 py-1"
        >
          <span class="font-weight-bolder mr-2">
            {{ selection.length }} dossier{{ selection.length > 1 ? 's' : '' }} sélectionné{{ selection.length > 1 ? 's' : '' }}
          </span>
          <span>
            <b-button
              size="sm"
              variant="outline-primary"
              class="mr-1 mb-25"
              :disabled="lotEnCours"
              @click="lancerLot('traiter')"
            >
              Marquer traité
            </b-button>
            <b-button
              size="sm"
              variant="outline-primary"
              class="mr-1 mb-25"
              :disabled="lotEnCours"
              @click="lancerLot('annulertraitement')"
            >
              Marquer non traité
            </b-button>
            <b-button
              size="sm"
              variant="outline-primary"
              class="mr-1 mb-25"
              :disabled="lotEnCours"
              @click="lancerLot('relance')"
            >
              Relancer
            </b-button>
            <b-button
              size="sm"
              variant="outline-secondary"
              class="mr-1 mb-25"
              :disabled="lotEnCours"
              @click="exporterCsv"
            >
              Exporter en CSV
            </b-button>
            <b-button
              size="sm"
              variant="outline-danger"
              class="mb-25"
              :disabled="lotEnCours"
              @click="ouvrirArchivage"
            >
              Archiver…
            </b-button>
          </span>
        </b-alert>

        <vue-good-table
          :columns="columns"
          :rows="rows"
          :select-options="{
            enabled: true,
            selectOnCheckboxOnly: true,
            selectionInfoClass: 'd-none',
            disableSelectInfo: true,
          }"
          :pagination-options="{
            enabled: true,
            perPage: pageLength,
            mode: 'records',
          }"
          :total-rows="total"
          mode="remote"
          @on-page-change="onPageChange"
          @on-per-page-change="onPerPageChange"
          @on-selected-rows-change="onSelectionChange"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <span v-if="props.column.field === 'D_date'">
              {{ formatDate(props.row.D_date) }}
            </span>
            <span v-else-if="props.column.field === 'client'">
              {{ props.row.C_prenom }} {{ props.row.C_nom }}<br>
              <small class="text-muted">{{ props.row.C_email }}</small>
            </span>
            <span v-else-if="props.column.field === 'I_etat'">
              <b-badge :variant="etatVariant(props.row.I_etat)">
                {{ etatLabel(props.row.I_etat) }}
              </b-badge>
            </span>
            <span v-else-if="props.column.field === 'I_etatMandat'">
              <b-badge :variant="mandatVariant(props.row.I_etatMandat)">
                {{ mandatLabel(props.row.I_etatMandat) }}
              </b-badge>
            </span>
            <span v-else-if="props.column.field === 'B_traite'">
              <b-badge :variant="props.row.B_traite ? 'success' : 'secondary'">
                {{ props.row.B_traite ? 'Oui' : 'Non' }}
              </b-badge>
            </span>
            <span v-else-if="props.column.field === 'B_archive'">
              <b-badge :variant="props.row.B_archive ? 'dark' : 'secondary'">
                {{ props.row.B_archive ? 'Oui' : 'Non' }}
              </b-badge>
            </span>
            <span v-else-if="props.column.field === 'actions'">
              <b-button
                size="sm"
                variant="primary"
                @click="voir(props.row.UI)"
              >
                Voir
              </b-button>
            </span>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </b-card>
    </b-container>

    <!-- Archivage en masse. Pas de window.confirm (interdit par l'ESLint du
         projet), et surtout : l'archivage SUPPRIME les pièces des clients du
         disque, définitivement. D'où la saisie du nombre, qui oblige à
         regarder ce qu'on détruit. -->
    <b-modal
      id="modal-archivage-lot"
      ref="modalArchivage"
      title="Archiver les dossiers sélectionnés"
      ok-variant="danger"
      ok-title="Archiver définitivement"
      cancel-title="Annuler"
      :ok-disabled="String(confirmationArchivage).trim() !== String(selection.length)"
      @ok="archiverLot"
      @hidden="confirmationArchivage = ''"
    >
      <p>
        Les pièces jointes de
        <strong>{{ selection.length }} dossier{{ selection.length > 1 ? 's' : '' }}</strong>
        vont être <strong>supprimées du disque</strong>. Cette opération est
        irréversible : les métadonnées sont conservées pour l'audit, mais les
        fichiers eux-mêmes ne seront plus récupérables.
      </p>
      <b-form-group :label="`Saisir « ${selection.length} » pour confirmer`">
        <b-form-input
          v-model="confirmationArchivage"
          :placeholder="String(selection.length)"
        />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormSelect, BFormInput, BButton, BBadge, BContainer,
  BAlert, BModal, BFormGroup,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AdminNav from './AdminNav.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormSelect,
    BFormInput,
    BButton,
    BBadge,
    BContainer,
    BAlert,
    BModal,
    BFormGroup,
    VueGoodTable,
    AdminNav,
  },
  data() {
    return {
      admin: null,
      rows: [],
      total: 0,
      // Sélection courante, alimentée par vue-good-table.
      selection: [],
      lotEnCours: false,
      confirmationArchivage: '',
      page: 1,
      pageLength: 25,
      searchDebounce: null,
      filters: {
        etat: -1,
        mandat: '',
        traite: -1,
        archive: -1,
        q: '',
      },
      optionsEtat: [
        { value: -1, text: 'Tous' },
        { value: 0, text: '0 — Créé' },
        { value: 1, text: '1 — Formule choisie' },
        { value: 2, text: '2 — Établissement' },
        { value: 3, text: '3 — Représentant' },
        { value: 4, text: '4 — Documents validés' },
        { value: 6, text: '6 — Payé' },
      ],
      // « À mettre en place » est la vue de travail : dossiers payés dont le
      // prélèvement demande une action — jamais demandé, ou en échec.
      // « Prêt à prélever » (état 2) n'en fait pas partie : pour SEPA, c'est
      // l'état normal d'un mandat valide tant qu'aucune collecte n'a eu lieu.
      optionsMandat: [
        { value: '', text: 'Tous' },
        { value: 'manquant', text: 'À mettre en place' },
        { value: 'attente', text: 'Prêt à prélever' },
        { value: 'actif', text: 'Actif' },
        { value: 'echec', text: 'En échec' },
      ],
      optionsTraite: [
        { value: -1, text: 'Tous' },
        { value: 0, text: 'Non traités' },
        { value: 1, text: 'Traités' },
      ],
      optionsArchive: [
        { value: -1, text: 'Tous' },
        { value: 0, text: 'Non archivés' },
        { value: 1, text: 'Archivés' },
      ],
      columns: [
        { label: 'Date', field: 'D_date' },
        { label: 'Client', field: 'client' },
        { label: 'Raison sociale', field: 'C_raisonSociale' },
        { label: 'Formule', field: 'pack' },
        { label: 'État', field: 'I_etat' },
        { label: 'Mandat', field: 'I_etatMandat' },
        { label: 'Traité', field: 'B_traite' },
        { label: 'Archivé', field: 'B_archive' },
        { label: 'Docs', field: 'nb_documents', type: 'number' },
        { label: 'Actions', field: 'actions', sortable: false },
      ],
    }
  },
  created() {
    const raw = localStorage.getItem('domAdminData')
    if (raw) this.admin = JSON.parse(raw)
    this.loadList()
  },
  methods: {
    authHeaders() {
      return { headers: { 'x-access-token': localStorage.getItem('domAdminToken') } }
    },
    loadList() {
      const params = {
        page: this.page,
        limit: this.pageLength,
        etat: this.filters.etat,
        mandat: this.filters.mandat,
        traite: this.filters.traite,
        archive: this.filters.archive,
        q: this.filters.q,
      }
      this.$http.get('/admindom/dossiers', { params, ...this.authHeaders() })
        .then(response => {
          if (response.data.res === 1) {
            this.rows = response.data.dossiers
            this.total = response.data.total
          }
        })
        .catch(err => this.handleAuthError(err))
    },
    onSearch() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => {
        this.page = 1
        this.loadList()
      }, 350)
    },
    onPageChange(params) {
      this.page = params.currentPage
      this.loadList()
    },
    onPerPageChange(params) {
      this.pageLength = params.currentPerPage
      this.page = 1
      this.loadList()
    },
    voir(UI) {
      this.$router.push({ name: 'admin-dom-dossier-detail', params: { UIdemande: UI } })
    },
    logout() {
      localStorage.removeItem('domAdminToken')
      localStorage.removeItem('domAdminData')
      this.$router.push({ name: 'admin-dom-login' })
    },
    handleAuthError(err) {
      if (err && err.response && (err.response.status === 401 || err.response.status === 403)) {
        this.logout()
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'Session expirée',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
      }
    },
    formatDate(d) {
      if (!d) return ''
      const dt = new Date(d)
      return `${dt.toLocaleDateString('fr-FR')} ${dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
    },
    etatLabel(etat) {
      const map = {
        0: 'Créé',
        1: 'Formule',
        2: 'Établissement',
        3: 'Représentant',
        4: 'Docs validés',
        5: 'Récap',
        6: 'Payé',
      }
      return map[etat] !== undefined ? map[etat] : etat
    },
    // Même motif que le toast de session expirée ci-dessous.
    showToast(message, variant = 'success') {
      const icones = {
        success: 'CheckCircleIcon',
        danger: 'AlertTriangleIcon',
        secondary: 'InfoIcon',
      }
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: {
          title: message,
          icon: icones[variant] || 'InfoIcon',
          variant,
        },
      })
    },

    onSelectionChange(params) {
      this.selection = (params.selectedRows || []).map(r => r.UI)
    },

    // Compte rendu d'un lot. Le backend renvoie le détail dossier par dossier :
    // on le résume, parce qu'un lot « à moitié réussi » doit se voir.
    annoncerResultat(action, data) {
      const { compte } = data
      const morceaux = [`${compte.ok} traité${compte.ok > 1 ? 's' : ''}`]
      if (compte.ignores > 0) morceaux.push(`${compte.ignores} ignoré${compte.ignores > 1 ? 's' : ''}`)
      if (compte.erreurs > 0) morceaux.push(`${compte.erreurs} en erreur`)
      if (data.nbFichiersSupprimes) morceaux.push(`${data.nbFichiersSupprimes} fichier(s) supprimé(s)`)
      this.showToast(morceaux.join(', '), compte.erreurs > 0 ? 'danger' : 'success')

      // Les motifs d'exclusion sont utiles : « déjà payé », « sans email »…
      // Sans eux, l'équipe ne saurait pas pourquoi 8 relances sur 12 sont
      // parties, et chercherait une panne là où il n'y en a pas.
      const ignores = (data.resultats || []).filter(r => r.etat === 'ignore' && r.motif)
      if (ignores.length > 0) {
        const motifs = [...new Set(ignores.map(r => r.motif))]
        this.showToast(`Ignorés : ${motifs.join(', ')}`, 'secondary')
      }
    },

    lancerLot(action, extra = {}) {
      if (this.selection.length === 0) return
      this.lotEnCours = true
      this.$http.post('/admindom/lot', { action, uis: this.selection, ...extra }, this.authHeaders())
        .then(response => {
          const { data } = response
          if (data.res !== 1) {
            this.showToast(data.message || 'Le traitement a échoué', 'danger')
            return
          }
          this.annoncerResultat(action, data)
          this.selection = []
          this.loadList()
        })
        .catch(err => {
          this.showToast('Le traitement a échoué', 'danger')
          this.handleAuthError(err)
        })
        .then(() => {
          this.lotEnCours = false
        })
    },

    ouvrirArchivage() {
      this.confirmationArchivage = ''
      this.$refs.modalArchivage.show()
    },

    archiverLot() {
      this.lancerLot('archiver', { confirmation: this.selection.length })
    },

    // Téléchargement : on passe par un Blob plutôt que par un lien direct,
    // parce que l'endpoint est en POST (il porte la sélection) et exige le
    // jeton admin — un simple href ne transmettrait ni l'un ni l'autre.
    exporterCsv() {
      if (this.selection.length === 0) return
      this.lotEnCours = true
      this.$http.post('/admindom/export-csv', { uis: this.selection }, {
        ...this.authHeaders(),
        responseType: 'blob',
      })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }))
          const lien = document.createElement('a')
          lien.href = url
          lien.setAttribute('download', `dossiers-${new Date().toISOString().slice(0, 10)}.csv`)
          document.body.appendChild(lien)
          lien.click()
          document.body.removeChild(lien)
          window.URL.revokeObjectURL(url)
          this.showToast(`${this.selection.length} dossier(s) exporté(s)`, 'success')
        })
        .catch(err => {
          this.showToast("L'export a échoué", 'danger')
          this.handleAuthError(err)
        })
        .then(() => {
          this.lotEnCours = false
        })
    },

    // Mêmes libellés que DossierDetail.vue (TB_DOM_DEVIS.I_etatMandat, sql/012).
    mandatLabel(etat) {
      const map = {
        0: '—',
        1: 'Non terminé',
        2: 'Prêt à prélever',
        3: 'Actif',
        '-1': 'Échec',
      }
      return map[etat] !== undefined ? map[etat] : `État ${etat}`
    },
    mandatVariant(etat) {
      if (etat === 3) return 'success'
      if (etat === 2) return 'info'
      if (etat === -1) return 'danger'
      if (etat === 1) return 'warning'
      return 'secondary'
    },
    etatVariant(etat) {
      if (etat >= 6) return 'success'
      if (etat === 4) return 'info'
      if (etat >= 1) return 'primary'
      return 'secondary'
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';

.admin-dom-wrapper {
  min-height: 100vh;
  background-color: #f8f8f8;
  padding-bottom: 2rem;
}
</style>
